import { PrimaryButton } from '@components/Buttons/PrimaryButton';
import { SwitchField } from '@components/Fields/SwitchField';
import { ModalWindow } from '@components/Modal/ModalWindow';
import { ModalWindowActions } from '@components/Modal/ModalWindowActions';
import { ModalWindowContent } from '@components/Modal/ModalWindowContent';
import { ModalWindowTitle } from '@components/Modal/ModalWindowTitle';
import { useEffectOnce } from '@hooks/useEffectOnce';
import { useField } from '@hooks/useField';
import { useForm } from '@hooks/useForm';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { IModalWindow } from '@hooks/useModalWindow';
import { useUtils } from '@hooks/useUtils';
import { useValidation } from '@hooks/useValidation';
import IFatura from '@model/Fatura';
import { EnumFormaPagamento, useFormaPagamentoCombo } from '@model/FormaPagamento';
import { EnumModoFechamentoNegativo, IRegistrarPagamento, IRegistrarPagamentoRequest, useFaturaService } from '@services/FaturaService';
import { yup } from '@validations/Yup';
import React from 'react';
import { RegistrarPagamentoForm } from './RegistrarPagamentoForm';

export type RegistrarPagamentoModalProps = {
    modal: IModalWindow
    fatura: IFatura
    onPagamentoRegistrado: (fatura: IFatura, valorRecebido: number, imprimirRecibo: boolean) => void
}

export function RegistrarPagamentoModal ({ modal, fatura, onPagamentoRegistrado }: RegistrarPagamentoModalProps) {
    const [confirmSectionVisible, setConfirmSectionVisible] = React.useState(false);
    const [isFormValid, setFormValid] = React.useState(false);
    
    const valorAtualField = useField(0);
    const saldoAtualField = useField(0);

    const loading = useLoading();
    const message = useMessage();
    const faturaService = useFaturaService();
    const formaPagamentoCombo = useFormaPagamentoCombo();
    const utils = useUtils();
    
    const form = useForm<IRegistrarPagamento>({
        valorRecebido: useField(0),
        formaPagamento: useField(EnumFormaPagamento.None),
        dataPagamento: useField(null),
        cienteCreditoCliente: useField(false),
        modoFechamentoNegativo: useField(),
        valorOriginal: useField(0),
        valor: useField(0),
        valorJuros: useField(0),
        valorMulta: useField(0),
        valorAtual: useField(0),
        valorPago: useField(fatura.valorPago),
        abonarJurosMulta: useField(fatura.abonarJurosMulta),
        abonarJurosMultaDesabilitado: useField(fatura.abonarJurosMultaDesabilitado),
        saldoAtual: useField(0),
        saldo: useField(0),
        novoSaldo: useField(0),
        notas: useField(),
        imprimirRecibo: useField("1")
    });

    const { fields } = form;

    const calcValorAtual = React.useCallback(() => {
        const valorAtual = fatura.valorOriginal + (fields.abonarJurosMulta.value !== "1" ? fields.valorMulta.value + fields.valorJuros.value : 0);
        valorAtualField.setValue(valorAtual);
        return valorAtual;
    }, [fatura.valorOriginal, fields.abonarJurosMulta.value, fields.valorJuros.value, fields.valorMulta.value, valorAtualField]);

    const calcSaldo = React.useCallback(() => {
        const saldo = calcValorAtual() - fatura.valorPago;
        saldoAtualField.setValue(saldo);
        return saldo;
    }, [calcValorAtual, fatura.valorPago, saldoAtualField]);

    const validation = useValidation<IRegistrarPagamento>({
        valorRecebido: yup.number().positive(),
        formaPagamento: yup.mixed().oneOf(Object.values(EnumFormaPagamento)) as any
    });

    const handleConfirm = React.useCallback(() => {
        if (!loading.isLoading) {
            const values = form.getInputValues();

            validation.validate(values)
                .then(() => {
                    loading.start();

                    const request = utils.transformObject<IRegistrarPagamento, IRegistrarPagamentoRequest>(values, [
                        "valorRecebido",
                        "formaPagamento",
                        "dataPagamento",
                        "abonarJurosMulta",
                        "cienteCreditoCliente",
                        "modoFechamentoNegativo"
                    ]);

                    faturaService.registrarPagamento(fatura.faturaId, request)
                        .then(response => {
                            message.success("Pagamento registrado com sucesso");
                            modal.close();
                            onPagamentoRegistrado(response.data, fields.valorRecebido.value, fields.imprimirRecibo.value === "1");
                        })
                        .catch(e => {
                            message.handleResponse(e)
                                .badRequest(form.setErrorsFromServer)
                                .showException();
                        })
                        .finally(loading.stop);
                })
                .catch(form.setErrorsFromYup);
        }
    }, [fatura.faturaId, faturaService, fields.imprimirRecibo.value, fields.valorRecebido.value, form, loading, message, modal, onPagamentoRegistrado, utils, validation]);

    React.useEffect(() => {
        let _confirmSectionVisible = false;

        const saldoCalculado = calcSaldo() - fields.valorRecebido.value;
        const novoSaldo = Math.abs(saldoCalculado) >= 0.01 ? saldoCalculado : 0;

        fields.novoSaldo.setValue(novoSaldo);

        if (fields.formaPagamento.value !== EnumFormaPagamento.None && novoSaldo !== 0) {
            if (fields.formaPagamento.value !== EnumFormaPagamento.Caixa) {
                validation.add("dataPagamento", yup.date().required());
            }
            else {
                validation.remove("dataPagamento");
            }

            const isValid = validation.isValid({
                valorRecebido: fields.valorRecebido.value,
                formaPagamento: fields.formaPagamento.value,
                dataPagamento: fields.dataPagamento.value
            });

            _confirmSectionVisible = isValid;
        }

        setConfirmSectionVisible(_confirmSectionVisible);
    }, [calcSaldo, fields.dataPagamento.value, fields.formaPagamento.value, fields.novoSaldo, fields.saldo, fields.valor.value, fields.valorPago.value, fields.valorRecebido.value, validation]);

    React.useEffect(() => {
        let _isFormValid = false;

        if (fields.formaPagamento.value !== EnumFormaPagamento.None) {
            if (fields.formaPagamento.value !== EnumFormaPagamento.Caixa) {
                validation.add("dataPagamento", yup.date().required());
            }
            else {
                validation.remove("dataPagamento");
            }

            const isValid = validation.isValid({
                valorRecebido: fields.valorRecebido.value,
                formaPagamento: fields.formaPagamento.value,
                dataPagamento: fields.dataPagamento.value
            });

            const saldoCalculado = calcSaldo() - fields.valorRecebido.value;
            const novoSaldo = Math.abs(saldoCalculado) >= 0.01 ? saldoCalculado : 0;

            _isFormValid = isValid && (
                novoSaldo === 0 ||
                (novoSaldo > 0 && fields.modoFechamentoNegativo.value !== undefined && fields.modoFechamentoNegativo.value !== EnumModoFechamentoNegativo.None) ||
                (novoSaldo < 0 && fields.cienteCreditoCliente.value === "1")
            )
        }

        setFormValid(_isFormValid);
    }, [calcSaldo, fields.cienteCreditoCliente.value, fields.dataPagamento.value, fields.formaPagamento.value, fields.modoFechamentoNegativo.value, fields.novoSaldo, fields.saldo.value, fields.valorRecebido.value, validation]);

    const handleEnter = React.useCallback(() => {
        form.setValues({
            valorRecebido: 0,
            formaPagamento: EnumFormaPagamento.None,
            dataPagamento: null,
            cienteCreditoCliente: false,
            modoFechamentoNegativo: EnumModoFechamentoNegativo.None,
            valorOriginal: fatura.valorOriginal,
            valor: fatura.valorTotal,
            valorJuros: fatura.saldoJurosCalculado,
            valorMulta: fatura.saldoMultaCalculado,
            valorAtual: calcValorAtual(),
            valorPago: fatura.valorPago,
            abonarJurosMulta: fatura.abonarJurosMulta,
            abonarJurosMultaDesabilitado: fatura.abonarJurosMultaDesabilitado,
            saldo: calcSaldo(),
            saldoAtual: calcSaldo(),
            novoSaldo: calcSaldo(),
            notas: "",
            imprimirRecibo: "1"
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fatura.faturaId]);

    React.useEffect(() => {
        if (fields.dataPagamento.value !== null && fields.dataPagamento.value !== undefined) {
            if (!loading.isLoading) {
                loading.start();
                faturaService.obterJurosMultaPorData(fatura.faturaId, fields.dataPagamento.value)
                    .then(response => {
                        form.fields.valorJuros.setValue(response.data.saldoJurosCalculado);
                        form.fields.valorMulta.setValue(response.data.saldoMultaCalculado);
                        calcValorAtual();
                        calcSaldo();
                    })
                    .catch(e => {
                        message.showException(e);
                        fields.dataPagamento.setValue(null);
                    })
                    .finally(loading.stop);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields.dataPagamento.value]);
    
    useEffectOnce(() => {
        formaPagamentoCombo.loadManuaisOptions();
    })

    return (
        <ModalWindow
            modal={modal}
            disabled={loading.isLoading}
            TransitionProps={{
                onEnter: handleEnter
            }}
        >
            <ModalWindowTitle
                title={`Registrar pagamento para a fatura ${fatura.faturaId}`}
            />

            <ModalWindowContent>
                <RegistrarPagamentoForm 
                    form={form}
                    formaPagamentoCombo={formaPagamentoCombo}
                    confirmSectionVisible={confirmSectionVisible}
                    valorAtualField={valorAtualField}
                    saldoAtualField={saldoAtualField}
                />
            </ModalWindowContent>

            <ModalWindowActions>
                <SwitchField
                    field={fields.imprimirRecibo}
                    label="Imprimir Recibo"
                    color="secondary"
                    updateFieldStateOnChange
                />
                <PrimaryButton onClick={handleConfirm} disabled={!isFormValid}>Registrar Pagamento</PrimaryButton>
            </ModalWindowActions>
        </ModalWindow>
    )
}